import { useEffect, useRef, useCallback, useState } from 'react';
import { buildProtectedStreamUrl } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ─── Guardian Button States ─────────────────────────────────────
export type GuardianButtonState =
  | 'idle'           // No run or last run is old
  | 'running'        // Currently executing
  | 'complete_clean' // Completed with 0 regressions
  | 'complete_regressions' // Completed with unfixable regressions
  | 'timed_out'      // Exceeded 5-minute timeout
  | 'error';         // Run failed

export type GuardianProgressPhase =
  | 'fetching_baseline'
  | 'running_tests'
  | 'analyzing'
  | 'fixing'
  | 'complete'
  | 'error';

export interface GuardianTestSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  duration_ms: number;
  coverage_percent?: number;
}

export interface GuardianSSEEvent {
  phase: string;
  status: string;
  progress_phase?: GuardianProgressPhase;
  regressions_found?: number;
  regressions_fixed?: number;
  regressions_unfixable?: number;
  regressions?: unknown[];
  test_summary?: GuardianTestSummary;
  cost_usd?: number;
  completed_at?: string;
  started_at?: string;
  error?: string;
  run_id?: string;
}

interface UseGuardianSSEOptions {
  /** Do not create a stream until the project has completed its first sync. */
  enabled?: boolean;
  runId: string | null;
  streamToken?: string | null;
  onEvent?: (event: GuardianSSEEvent) => void;
  onComplete?: (event: GuardianSSEEvent) => void;
  onError?: (error: string) => void;
}

export function useGuardianSSE({ enabled = true, runId, streamToken, onEvent, onComplete, onError }: UseGuardianSSEOptions) {
  const sourceRef = useRef<EventSource | null>(null);
  const [buttonState, setButtonState] = useState<GuardianButtonState>('idle');
  const [lastEvent, setLastEvent] = useState<GuardianSSEEvent | null>(null);
  const [allEvents, setAllEvents] = useState<GuardianSSEEvent[]>([]);

  const cleanup = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    // An unsynced project has no Guardian run to stream. In particular, do not
    // construct EventSource here: strict stream-token auth turns that into a
    // noisy 401 before the user has any project knowledge.
    if (!enabled || !runId) {
      cleanup();
      return;
    }

    cleanup();
    setButtonState('running');
    setAllEvents([]);

    const url = buildProtectedStreamUrl(API_BASE, `/api/guardian/stream/${runId}`, streamToken);
    if (!url) {
      setButtonState('error');
      onError?.('Live Guardian updates are unavailable. Refresh to retry.');
      return;
    }
    const es = new EventSource(url);
    sourceRef.current = es;

    es.addEventListener('guardian_event', (e) => {
      try {
        const data: GuardianSSEEvent = JSON.parse(e.data);
        setLastEvent(data);
        setAllEvents(prev => [...prev, data]);
        onEvent?.(data);

        if (data.status === 'complete_clean') {
          setButtonState('complete_clean');
          cleanup();
          onComplete?.(data);
        } else if (data.status === 'complete_regressions') {
          setButtonState('complete_regressions');
          cleanup();
          onComplete?.(data);
        } else if (data.status === 'timed_out') {
          setButtonState('timed_out');
          cleanup();
          onError?.(data.error || 'Guardian run timed out');
        } else if (data.status === 'error') {
          setButtonState('error');
          cleanup();
          onError?.(data.error || 'Guardian run failed');
        } else if (data.status === 'running' || data.status === 'started') {
          setButtonState('running');
        }
      } catch {
        // Non-JSON data — ignore
      }
    });

    es.addEventListener('keepalive', () => {
      // Keep connection alive — no action needed
    });

    es.onerror = () => {
      es.close();
      sourceRef.current = null;
      // Don't change button state on SSE error — the run might still be going
      // The status endpoint will resolve the correct state on next page load
    };

    return () => {
      cleanup();
    };
  }, [enabled, runId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { buttonState, setButtonState, lastEvent, allEvents, cleanup };
}
